import { useEffect, useRef, useState } from 'react';
import { Mic, Play, Square } from 'lucide-react';
import {
  deleteVoiceNote, playVoiceNote, useVoiceNotes, type VoiceNote,
} from '@core/firebase/voiceNotes';
import { useLanguage } from '@/contexts/LanguageContext';
import { ConfirmButton } from '@/components/ConfirmButton';
import { CardTitle, Notice, SoftCard, pill } from '@/components/PageKit';
import { cn } from '@/lib/cn';

/**
 * A guardian listening to what the teacher said about their child —
 * م-3-04 to م-3-08.
 *
 * The recording is fetched only when somebody presses play, and it is held
 * in the page for exactly as long as it is playing. There is no link and no
 * download control, because the server never issues a URL: م-3-07 is a
 * property of the system, and this component simply does not undo it.
 */

/** How long the whole thing lives before it is deleted — م-3-08. */
const RETENTION_DAYS = 30;

export function VoiceNotes({ studentId }: { studentId: string | null }) {
  const { t, language } = useLanguage();
  const { notes, loading } = useVoiceNotes(studentId);

  const [playing, setPlaying] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [deleted, setDeleted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  /** A blob left behind is a recording of a child still in this tab. */
  const release = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setPlaying(null);
  };

  // Leaving the page ends the playback and frees the recording with it.
  useEffect(() => release, []);

  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);
  const when = (ms: number) => new Intl.DateTimeFormat(
    language === 'ar' ? 'ar-SA-u-nu-latn' : 'en-GB',
    { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
  ).format(new Date(ms));

  const listen = async (note: VoiceNote) => {
    if (busy) return;
    if (playing === note.id) { release(); return; }
    release();
    setBusy(note.id);
    setErrorKey(null);
    try {
      const played = await playVoiceNote(note.id);
      const bytes = Uint8Array.from(atob(played.audio), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: played.contentType }));
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = release;
      setPlaying(note.id);
      await audio.play();
    } catch {
      release();
      setErrorKey('voiceNotes.playFailed');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (noteId: string) => {
    setErrorKey(null);
    setDeleted(false);
    try {
      if (playing === noteId) release();
      await deleteVoiceNote(noteId);
      setDeleted(true);
    } catch {
      setErrorKey('voiceNotes.deleteFailed');
    }
  };

  return (
    <SoftCard className="space-y-4">
      <CardTitle
        icon={<Mic />}
        trailing={(
          <span className="shrink-0 text-xs text-helper">
            {t('voiceNotes.expiresIn', { count: RETENTION_DAYS, n: count(RETENTION_DAYS) })}
          </span>
        )}
      >
        {t('voiceNotes.title')}
      </CardTitle>

      {errorKey && <Notice tone="danger" testId="note-error">{t(errorKey)}</Notice>}
      {deleted && <Notice tone="ok" testId="note-deleted">{t('voiceNotes.deleted')}</Notice>}

      {loading ? (
        <div data-testid="notes-loading" className="h-12 animate-pulse rounded-[14px] bg-surface" />
      ) : notes.length === 0 ? (
        <div data-testid="no-notes" className="rounded-[18px] bg-surface px-4 py-5 text-center">
          <p className="font-semibold text-ink">{t('voiceNotes.none')}</p>
          <p className="mt-1 text-sm text-helper">{t('voiceNotes.noneBody')}</p>
        </div>
      ) : (
        <ul className="divide-y divide-hair">
          {notes.map((note) => (
            <li
              key={note.id}
              data-testid={`note-${note.id}`}
              className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
            >
              <div className="min-w-0">
                <p className="text-ink">
                  {when(note.createdAt)}
                  <span className="ms-2 text-helper">
                    {t('voiceNotes.seconds', {
                      count: Math.round(note.durationMs / 1000),
                      n: count(Math.round(note.durationMs / 1000)),
                    })}
                  </span>
                </p>
                {/* م-3-06's in-app half: what the guardian has not heard yet. */}
                <p
                  data-testid={`note-state-${note.id}`}
                  className={note.heardAt === null
                    ? 'mt-1 inline-flex rounded-full bg-lime px-2.5 py-0.5 text-xs font-semibold text-accent-foreground'
                    : 'mt-1 text-xs text-helper'}
                >
                  {note.heardAt === null ? t('voiceNotes.new') : t('voiceNotes.heard')}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  data-testid={`play-${note.id}`}
                  onClick={() => { void listen(note); }}
                  disabled={busy !== null}
                  aria-busy={busy === note.id}
                  className={cn(pill.base, pill.sm, pill.lime)}
                >
                  {playing === note.id
                    ? <Square className="h-4 w-4" />
                    : <Play className="h-4 w-4" />}
                  {playing === note.id ? t('voiceNotes.stop') : t('voiceNotes.play')}
                </button>
                {/* أ-04: deleting is the guardian's to ask for, and it takes
                    the recording with it. */}
                <ConfirmButton
                  testId={`delete-${note.id}`}
                  label={t('voiceNotes.delete')}
                  question={t('voiceNotes.confirmDelete')}
                  tone="danger"
                  size="sm"
                  onConfirm={() => { void remove(note.id); }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="text-center text-xs text-helper">{t('voiceNotes.inAppOnly')}</p>
    </SoftCard>
  );
}
